import type { TelemetryOptions } from "@smakecloud/browser-telemetry";
import type { JWTPayload } from "~/stores/useTokenStore";

/** Faro app name = browser `service.name`. The Nitro server reports as "master-administration". */
export const TELEMETRY_APP_NAME = "master-administration-browser";

/** Same-origin beacon proxy, `server/routes/collect.post.ts` → in-cluster Alloy faro.receiver. */
export const TELEMETRY_COLLECTOR_URL = "/collect";

// Nuxt chunks, the beacons themselves and the Font Awesome kit: no spans
export const TELEMETRY_IGNORE_URLS = [/\/_nuxt\//, /\/collect$/, /fontawesome/];

/** `runtimeConfig.public.telemetry`; Nuxt parses the NUXT_PUBLIC_TELEMETRY_* values ("true" → true, "0.2" → 0.2). */
export type TelemetryConfig = {
    enabled?: boolean | string;
    environment?: string;
    sampleRate?: number | string;
    version?: number | string;
};

/** Faro options from the public runtime config, or undefined when telemetry is off (the default). */
export function telemetryOptions(config: {
    masterApiBaseUrl?: string;
    telemetry?: TelemetryConfig;
}): TelemetryOptions | undefined {
    const telemetry = config.telemetry;

    if (telemetry?.enabled !== true && telemetry?.enabled !== "true") {
        return undefined;
    }

    return {
        // traceparent only to the master API (smake CORS allows it)
        apiOrigins: [apiOrigin(config.masterApiBaseUrl)],
        appName: TELEMETRY_APP_NAME,
        collectorUrl: TELEMETRY_COLLECTOR_URL,
        environment: String(telemetry.environment ?? ""),
        ignoreUrls: TELEMETRY_IGNORE_URLS,
        sampleRate: telemetry.sampleRate,
        version: String(telemetry.version || "dev"),
    };
}

/** Faro user from the token: the staff user id and the master (app_id). Ids only, never e-mail or name. */
export function telemetryUser(
    payload: Pick<JWTPayload, "app_id" | "user_id"> | undefined,
): { attributes: { master_id: string }; id: string } | undefined {
    if (payload?.user_id === undefined) {
        return undefined;
    }

    return {
        attributes: { master_id: String(payload.app_id ?? "") },
        id: String(payload.user_id),
    };
}

function apiOrigin(url: string | undefined): string | undefined {
    if (!url) {
        return undefined;
    }

    try {
        return new URL(url).origin;
    } catch {
        return undefined;
    }
}
